/**
 * General — who this Company is, and the one thing about it set up once
 * and then left alone: the people who can sign in.
 *
 * That is a page below this screen rather than rows on it: it is
 * answered on the day a Company starts and touched about never after.
 * Which page is showing is a URL place (`?page=`, committed with
 * `replace`), so a page is linkable and survives Back exactly as
 * `?section=` does. The rental system and the calendar were pages here
 * too, until they went to Settings → Connections: a Company looking for
 * the thing it plugged in looks for that word in the sidebar, not under
 * its own name and logo.
 *
 * The notification address is not here either: it went to Settings →
 * Notifications, beside the switch that stops that email and the list of
 * everyone else copied on it. "Who is told about a new job" was being
 * answered on two screens, and this was the one without the list.
 *
 * There is no reply-address setting. Hitting Reply on a New Inquiry
 * reaches the Customer, because their own address is the reply line; the
 * Company's notification address stands in only when the Customer left
 * none, and that is the address on Notifications rather than a second
 * one to keep in step with it.
 *
 * There is deliberately no GST number, NZBN, or bank account here. Those
 * print on a money document, and intake never prints one — whichever
 * rental system a Company runs is where its quote comes from, and for
 * most Companies that is not the HireOS RMS.
 *
 * Picking a logo file uploads it immediately; there is no separate upload
 * step or staged file state.
 */
import { useRef, useState } from "react";
import { useSearchParams } from "react-router";
import { Button } from "../../components/ui/Button";
import { ConfirmModal } from "../../components/ui/ConfirmModal";
import { SettingRow, TextSettingRow } from "../../components/ui/SettingRow";
import { TrashIcon } from "../../components/ui/icons";
import {
  removeCompanyLogo,
  uploadCompanyLogo,
  type CompanySettings,
  type CompanySettingsUpdate,
} from "../../lib/settings-api";
import { useAction } from "../../lib/use-action";
import { useSettingCommit } from "../../lib/use-setting-commit";
import { BackLink, DrillDownRow, DrillList, SectionHeader } from "./section-chrome";
import { UsersSection } from "./UsersSection";
import styles from "./GeneralSection.module.css";

export interface SectionProps {
  settings: CompanySettings;
  onPatch: (patch: CompanySettingsUpdate) => Promise<void>;
}

/** The pages General opens, as they read in `?page=`. */
const PAGES = [
  {
    id: "users",
    name: "Users",
    description: "Who can sign in to this workspace.",
    render: () => <UsersSection />,
  },
];

export function GeneralSection({
  settings,
  onPatch,
  onLogoChanged,
}: SectionProps & { onLogoChanged: (logoURL: string) => void }) {
  const [searchParams, setSearchParams] = useSearchParams();

  /** `?page=` is set and cleared in place: a drill is not history. */
  const showPage = (page: string | null) => {
    const next = new URLSearchParams(searchParams);
    if (page === null) next.delete("page");
    else next.set("page", page);
    setSearchParams(next, { replace: true });
  };

  const page = PAGES.find((p) => p.id === searchParams.get("page"));
  if (page) {
    return (
      <div>
        <BackLink label="General" onClick={() => showPage(null)} />
        {page.render()}
      </div>
    );
  }

  return (
    <section>
      <SectionHeader
        title="General"
        description="Your company name and logo, and who can sign in."
      />

      <TextSettingRow
        id="company-name"
        title="Company name"
        description="What callers hear, and the name your enquiry emails come from."
        value={settings.name}
        required
        onSave={(name) => onPatch({ name })}
      />
      <LogoRow settings={settings} onLogoChanged={onLogoChanged} />

      {/* No group heading: a short list of named rows does not need a
          name and a description above it saying the same thing twice. */}
      <DrillList>
        {PAGES.map((p) => (
          <DrillDownRow
            key={p.id}
            name={p.name}
            description={p.description}
            onOpen={() => showPage(p.id)}
          />
        ))}
      </DrillList>
    </section>
  );
}

export function LogoRow({
  settings,
  onLogoChanged,
}: {
  settings: CompanySettings;
  onLogoChanged: (logoURL: string) => void;
}) {
  const [confirmRemove, setConfirmRemove] = useState(false);
  const {
    working: removing,
    error: removeError,
    setError: setRemoveError,
    run: runRemove,
  } = useAction();
  const inputRef = useRef<HTMLInputElement>(null);
  // draft = the displayed logo URL; resyncs when the parent settings change.
  const logo = useSettingCommit<string>(settings.logo_url);

  /** Validate and upload in one step — picking the file IS the commit. */
  const upload = async (file: File | null) => {
    if (!file) return;
    if (file.type && !["image/png", "image/jpeg"].includes(file.type)) {
      logo.setError("Choose a PNG or JPEG image.");
      return;
    }
    if (file.size > 1024 * 1024) {
      logo.setError("The logo must be 1 MB or smaller.");
      return;
    }
    await logo.run(async () => {
      const nextURL = await uploadCompanyLogo(file);
      logo.setDraft(nextURL);
      onLogoChanged(nextURL);
    });
    if (inputRef.current) inputRef.current.value = "";
  };

  // Modal-confirmed destructive action: its failure renders inside the
  // modal (DESIGN.md error placement), never on the row behind it.
  const remove = async () => {
    await runRemove(async () => {
      await removeCompanyLogo();
      logo.setDraft("");
      onLogoChanged("");
      setConfirmRemove(false);
    });
  };

  return (
    <SettingRow
      title="Logo"
      description="Sits at the top of your enquiry emails. PNG or JPEG, up to 1 MB and 2000 × 2000 px."
      error={logo.error}
    >
      <span className={styles.logoControls}>
        <input
          ref={inputRef}
          type="file"
          accept="image/png,image/jpeg"
          aria-label="Logo file"
          className={styles.fileInput}
          disabled={logo.saving}
          onChange={(event) => void upload(event.target.files?.[0] ?? null)}
        />
        {logo.draft && (
          <img
            src={logo.draft}
            alt={`${settings.name} logo`}
            className={styles.logo}
          />
        )}
        <Button
          size="sm"
          variant="secondary"
          loading={logo.saving}
          onClick={() => inputRef.current?.click()}
        >
          {logo.draft ? "Replace" : "Upload logo"}
        </Button>
        {logo.draft && (
          <button
            type="button"
            aria-label="Remove logo"
            disabled={logo.saving}
            className={styles.removeLogo}
            onClick={() => setConfirmRemove(true)}
          >
            <TrashIcon className={styles.icon} />
          </button>
        )}
      </span>

      <ConfirmModal
        open={confirmRemove}
        title="Remove Company logo?"
        message="Your emails go back to a plain text header."
        confirmLabel="Remove logo"
        loading={removing}
        error={removeError}
        onConfirm={() => void remove()}
        onCancel={() => {
          setConfirmRemove(false);
          setRemoveError(null);
        }}
      />
    </SettingRow>
  );
}
